/**
 * Web-shop card — the organizer's whole catalogue behind one link and one QR code.
 *
 * Sits above the product list. Distinct from the per-product QR codes, which stay exactly as
 * they are: a sticker sells one item, this sells everything. Both run side by side on purpose
 * so their conversion can be compared (see ONSITE_KIOSK_DESIGN.md §1).
 *
 * The slug is allocated server-side the first time this loads, so an organizer never has to
 * "create" a shop — opening the page is enough.
 */
import React from "react";
import { useTranslation } from "react-i18next";
import { ArrowTopRightOnSquareIcon, QrCodeIcon } from "@heroicons/react/24/outline";

import { TwButton } from "../../components/tailwind";
import { useToast } from "../../provider/ToastProvider";
import { getMyWebShop } from "../../utils/productsApi";
import { buildLabelledQrPngFromUrl, downloadQr } from "./ProductQrModal";

export default function WebShopCard() {
  const { t } = useTranslation();
  const { addSuccessToast, addErrorToast } = useToast();

  const [shop, setShop] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [qrDataUrl, setQrDataUrl] = React.useState("");
  const [showQr, setShowQr] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      const result = await getMyWebShop();
      if (!result.error) setShop(result.data || result);
      setLoading(false);
    })();
  }, []);

  const url = shop?.url || "";
  const shopLabel = t("products.webShopQrLabel", "Shop here");

  // Rendered lazily and cached: drawing the canvas costs nothing until someone asks to see
  // or download it, and most visits to this page do neither.
  const ensureQr = React.useCallback(async () => {
    if (qrDataUrl) return qrDataUrl;
    const dataUrl = await buildLabelledQrPngFromUrl(url, shopLabel);
    setQrDataUrl(dataUrl);
    return dataUrl;
  }, [qrDataUrl, url, shopLabel]);

  if (loading || !url) return null;

  const sellableCount = parseInt(shop?.sellableCount || 0);

  return (
    <div className="w-full mb-5 p-4 bg-white border-2 border-gray-200 rounded-2xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-gray-900">
            {t("products.myWebShop", "My web shop")}
          </h2>
          <p className="text-sm text-gray-400 mt-0.5">
            {t(
              "products.myWebShopSubtitle",
              "One link and one QR code for your whole catalogue. Customers pick what they want and pay from their own phone."
            )}
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <code className="text-sm text-gray-700 bg-gray-50 rounded-lg px-2 py-1 break-all">
              {url}
            </code>
            <button
              type="button"
              className="text-sm text-primary hover:underline"
              onClick={() => {
                navigator.clipboard.writeText(url);
                addSuccessToast && addSuccessToast(t("myEvents.copied", "Copied"));
              }}
            >
              {t("myEvents.copy", "Copy")}
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <TwButton
            variant="secondary-outline"
            onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
          >
            <ArrowTopRightOnSquareIcon className="size-5" />
            &nbsp;{t("products.previewShop", "Preview shop")}
          </TwButton>

          <TwButton
            variant="secondary"
            onClick={async () => {
              const dataUrl = await ensureQr();
              if (!dataUrl) {
                addErrorToast && addErrorToast(t("errors.somethingIsWrong"));
                return;
              }
              setShowQr((open) => !open);
            }}
          >
            <QrCodeIcon className="size-5" />
            &nbsp;{showQr ? t("products.hideQr", "Hide QR") : t("products.viewQr", "View QR")}
          </TwButton>
        </div>
      </div>

      {/* An empty catalogue behind a printed code is a support call waiting to happen, so it
          is called out here rather than discovered by a customer at the counter. */}
      {sellableCount === 0 ? (
        <p className="mt-3 text-sm text-amber-700">
          {t(
            "products.webShopEmpty",
            "Your shop has no items for sale yet. Add a product with a price before printing this code."
          )}
        </p>
      ) : null}

      {showQr && qrDataUrl ? (
        <div className="mt-4 pt-4 border-t border-gray-200 flex flex-col items-center gap-3">
          <img
            src={qrDataUrl}
            alt={t("products.myWebShop", "My web shop")}
            className="w-56 h-auto"
          />
          <TwButton
            variant="secondary-outline"
            onClick={() => downloadQr(qrDataUrl, shop?.slug || "webshop")}
          >
            {t("products.downloadPng", "Download PNG")}
          </TwButton>
        </div>
      ) : null}
    </div>
  );
}
